import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { QRCodeSVG } from "qrcode.react";
import { EsimOnboarding } from "../components/EsimOnboarding";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
    Smartphone, Check, Zap, ArrowRight, ArrowLeft, Wifi, Search,
    ArrowRightLeft, Loader2, CheckCircle2, Signal, Plus,
} from "lucide-react";

const STEPS = ["Plan", "Device", "Number", "Review"];

function ActivationTimeline({ activation }) {
    if (!activation || !activation.stages) return null;
    const stages = activation.stages;
    return (
        <div className="border border-[#DDD6FE] rounded-sm bg-paper p-6" data-testid="activation-timeline">
            <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-xl">Activation progress</h3>
                {activation.service_active
                    ? <span className="font-mono text-[10px] uppercase tracking-wider text-brand-green flex items-center gap-1.5" data-testid="activation-service-active"><CheckCircle2 className="w-3.5 h-3.5" /> Service active</span>
                    : <span className="font-mono text-[10px] uppercase tracking-wider text-[#4C1D95]">In progress</span>}
            </div>
            <ol className="space-y-0">
                {stages.map((s, i) => (
                    <li key={s.key} className="flex gap-3" data-testid={`stage-${s.key}`}>
                        <div className="flex flex-col items-center">
                            <span className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                                s.state === "done" ? "bg-brand-green text-white"
                                : s.state === "current" ? "bg-[#4C1D95] text-white"
                                : "bg-zinc-200 text-zinc-500"}`}>
                                {s.state === "done" ? <Check className="w-4 h-4" />
                                    : s.state === "current" ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    : <span className="text-xs font-mono">{i + 1}</span>}
                            </span>
                            {i < stages.length - 1 && <span className={`w-px flex-1 min-h-[18px] ${s.state === "done" ? "bg-brand-green" : "bg-[#DDD6FE]"}`} />}
                        </div>
                        <div className="pb-4">
                            <p className={`text-sm font-medium ${s.state === "pending" ? "text-zinc-400" : "text-zinc-800"}`} data-testid={`stage-${s.key}-state`} data-state={s.state}>{s.label}</p>
                            {(s.detail || s.state === "current") && (
                                <p className="text-xs text-zinc-500">{s.state === "current" ? (s.detail || "In progress…") : s.detail}</p>
                            )}
                        </div>
                    </li>
                ))}
            </ol>
        </div>
    );
}

export function ByonBanner({ onCta }) {
    return (
        <div className="border border-brand-green bg-gradient-to-r from-[#ECFDF5] to-paper rounded-sm p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="byon-banner">
            <div className="flex items-center gap-3">
                <span className="text-2xl" aria-hidden>🎉</span>
                <div>
                    <p className="font-display text-lg leading-tight"><span className="font-serif-italic text-brand-green">No contract.</span> Month-to-month on every plan.</p>
                    <p className="text-sm text-zinc-600 mt-0.5">Pick any FlexLine Mobile plan, activate your eSIM in minutes, keep your number — paid upfront, activated automatically. <span className="font-medium">No contract, cancel anytime.</span></p>
                </div>
            </div>
            {onCta && (
                <button onClick={onCta} data-testid="byon-banner-cta"
                    className="bg-brand-green text-white hover:bg-brand-green rounded-sm h-10 px-5 text-sm font-medium shrink-0">
                    See plans
                </button>
            )}
        </div>
    );
}

export const CARRIER_LINKS = [
    { name: "Verizon", url: "https://www.verizon.com/signin" },
    { name: "AT&T", url: "https://www.att.com/my/#/login" },
    { name: "T-Mobile", url: "https://www.t-mobile.com/login" },
    { name: "Metro", url: "https://www.metrobyt-mobile.com/account/login" },
    { name: "Cricket", url: "https://www.cricketwireless.com/my-account" },
    { name: "Boost", url: "https://www.boostmobile.com/account/login.html" },
    { name: "Straight Talk", url: "https://www.straighttalk.com/sign-in" },
];

export default function Mobile() {
    const [loading, setLoading] = useState(true);
    const [subs, setSubs] = useState([]);
    const [plans, setPlans] = useState([]);
    const [fees, setFees] = useState({ regulatory_monthly: 3.99, activation_one_time: 15, data_per_gb: 15 });
    const [billUp, setBillUp] = useState({ path: "", name: "", busy: false });
    const [step, setStep] = useState(0);

    const [planId, setPlanId] = useState(() => {
        const p = new URLSearchParams(window.location.search).get("plan") || localStorage.getItem("flex_pending_plan");
        localStorage.removeItem("flex_pending_plan");
        return p;
    });
    const [device, setDevice] = useState({ imei: "", eid: "", device_model: "" });
    const [deviceResult, setDeviceResult] = useState(null);
    const [provisionType, setProvisionType] = useState(null); // new | port
    const [areaCode, setAreaCode] = useState("");
    const [numbers, setNumbers] = useState([]);
    const [searching, setSearching] = useState(false);
    const [chosenNumber, setChosenNumber] = useState(null);
    const [senior, setSenior] = useState(false);
    const [port, setPort] = useState({ phone_number: "", port_carrier: "", port_account_number: "", port_pin: "", port_zip: "", port_name: "", port_street: "", port_city: "", port_state: "" });
    const [loaSig, setLoaSig] = useState({ name: "", agreed: false });
    const [checkingOut, setCheckingOut] = useState(false);
    const [promo, setPromo] = useState("");
    const [promoInfo, setPromoInfo] = useState(null);
    const [selectedLineId, setSelectedLineId] = useState(null);
    const [adding, setAdding] = useState(() => !!new URLSearchParams(window.location.search).get("plan"));

    useEffect(() => {
        (async () => {
            try {
                const [s, p] = await Promise.all([api.get("/mobile/subscription"), api.get("/mobile/plans")]);
                setSubs(s.data.subscriptions || []);
                setPlans(p.data.plans || []);
                if (p.data.fees) setFees(p.data.fees);
            } catch { /* ignore */ }
            setLoading(false);
        })();
    }, []);

    const applyPromo = async (raw) => {
        const code = (raw ?? promo).trim().toUpperCase();
        setPromo(code);
        if (!code) { setPromoInfo(null); return; }
        try {
            const { data } = await api.get(`/promo/validate/${encodeURIComponent(code)}`);
            if (data.valid) { setPromoInfo(data); toast.success(`Promo applied: ${data.label}`); }
            else { setPromoInfo({ valid: false }); toast.error("That promo code isn't valid"); }
        } catch { setPromoInfo(null); }
    };

    useEffect(() => {
        const saved = localStorage.getItem("flexline_promo_code");
        if (saved) applyPromo(saved);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const lines = subs.filter((s) => ["active", "trialing", "past_due"].includes(s.status));

    const checkDevice = async () => {
        try {
            const { data } = await api.post("/mobile/device/check", device);
            setDeviceResult(data);
        } catch { toast.error("Device check failed"); }
    };

    const searchNumbers = async () => {
        setSearching(true);
        try {
            const { data } = await api.get(`/mobile/numbers/available?limit=12${areaCode ? `&area_code=${areaCode}` : ""}`);
            setNumbers(data.numbers || []);
            if (!data.numbers?.length) toast.info("No numbers for that area code — try another.");
        } catch { toast.error("Number search unavailable"); }
        setSearching(false);
    };

    const checkout = async () => {
        setCheckingOut(true);
        try {
            const payload = {
                plan_id: planId,
                provision_type: provisionType,
                senior,
                ...device,
                device_eid: device.eid, device_imei: device.imei, device_model: device.device_model,
                phone_number: provisionType === "new" ? chosenNumber : port.phone_number,
                ...(provisionType === "port" ? { ...port, loa_signature: loaSig.name, loa_agreed: loaSig.agreed, port_bill_path: billUp.path } : {}),
                promo_code: promoInfo?.valid ? promo : "",
            };
            const { data } = await api.post("/mobile/checkout", { ...payload, quiz_plan: localStorage.getItem("flex_quiz_plan") || "", source: localStorage.getItem("flex_src") || "" });
            localStorage.removeItem("flex_quiz_plan");
            window.location.href = data.url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Checkout failed");
            setCheckingOut(false);
        }
    };

    if (loading) return <div className="py-20 text-center text-sm text-zinc-500">Loading Flex Line Mobile…</div>;
    if (lines.length && !adding) {
        return <LinesView subs={lines} plans={plans} selectedId={selectedLineId} onSelect={setSelectedLineId}
            onAdd={() => { setAdding(true); setStep(0); setPlanId(null); setProvisionType(null); setChosenNumber(null); }} />;
    }

    const canNext =
        (step === 0 && planId) ||
        (step === 1 && deviceResult?.esim_supported) ||
        (step === 2 && ((provisionType === "new" && chosenNumber) || (provisionType === "port" && port.phone_number && port.port_carrier && port.port_pin && port.port_account_number && port.port_name && port.port_street && port.port_city && port.port_state && port.port_zip && billUp.path))) ||
        step === 3;

    return (
        <div className="space-y-8" data-testid="mobile-root">
            {adding && (
                <button onClick={() => setAdding(false)} data-testid="mobile-back-to-lines"
                    className="inline-flex items-center gap-1.5 text-sm text-[#4C1D95] hover:underline">
                    <ArrowLeft className="w-4 h-4" /> Back to my lines
                </button>
            )}
            <div>
                <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-deep mb-2 flex items-center gap-1.5">
                    <Signal className="w-3.5 h-3.5" /> Flex Line Mobile
                </p>
                <h1 className="font-display text-4xl font-light tracking-tight" data-testid="mobile-wizard-title">
                    {adding ? <>Add another <span className="font-serif-italic">line.</span></> : <>Your phone plan, <span className="font-serif-italic">reimagined.</span></>}
                </h1>
                <p className="text-zinc-600 mt-2 text-sm max-w-2xl">
                    eSIM mobile data + unlimited talk &amp; text through the Flex Line app. No store visit, no physical SIM — activate in minutes.
                </p>
            </div>

            <ByonBanner onCta={() => { setStep(0); }} />

            {/* Stepper */}
            <div className="flex items-center gap-2" data-testid="mobile-stepper">
                {STEPS.map((s, i) => (
                    <React.Fragment key={s}>
                        <div className={`flex items-center gap-2 ${i <= step ? "text-[#4C1D95]" : "text-zinc-400"}`}>
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-mono ${i < step ? "bg-emerald-deep text-white" : i === step ? "bg-[#4C1D95] text-white" : "bg-zinc-200"}`}>
                                {i < step ? <Check className="w-3 h-3" /> : i + 1}
                            </span>
                            <span className="text-sm hidden sm:inline">{s}</span>
                        </div>
                        {i < STEPS.length - 1 && <div className="flex-1 h-px bg-[#DDD6FE]" />}
                    </React.Fragment>
                ))}
            </div>

            {/* Step 0: Plans */}
            {step === 0 && (
                <div className="space-y-4">
                    <div className={`grid gap-4 ${plans.length > 1 ? "md:grid-cols-3" : "max-w-md"}`} data-testid="mobile-plans">
                        {plans.map((p) => {
                            const showSenior = senior && p.senior_price;
                            return (
                                <button key={p.id} onClick={() => setPlanId(p.id)} data-testid={`plan-${p.id}`}
                                    className={`text-left border rounded-sm p-5 relative transition-colors ${planId === p.id ? "border-[#4C1D95] bg-[#F3E8FF]" : "border-[#DDD6FE] bg-paper hover:bg-cream"}`}>
                                    {p.popular && <span className="absolute -top-2 left-4 bg-[#D97706] text-white text-[10px] font-mono uppercase px-2 py-0.5 rounded-sm">Most popular</span>}
                                    {p.senior_price && <span className="absolute -top-2 right-4 bg-emerald-deep text-white text-[10px] font-mono uppercase px-2 py-0.5 rounded-sm">Senior 62+</span>}
                                    <h3 className="font-display text-xl">{p.name}</h3>
                                    <div className="mt-1">
                                        {showSenior ? (
                                            <><span className="font-display text-3xl font-light text-emerald-deep">${p.senior_price}</span><span className="text-zinc-400 text-sm line-through ml-2">${p.price}</span><span className="text-zinc-500 text-sm">/mo</span></>
                                        ) : (
                                            <><span className="font-display text-3xl font-light">${p.price}</span><span className="text-zinc-500 text-sm">/mo</span></>
                                        )}
                                    </div>
                                    <p className="text-sm text-emerald-deep font-medium mt-1">{p.data} · {p.speed}</p>
                                    <p className="text-xs text-zinc-500 mt-1">{p.best_for}</p>
                                    <ul className="mt-4 space-y-1.5">
                                        {p.features.map((f) => <li key={f} className="text-sm flex items-start gap-2"><Check className="w-4 h-4 text-emerald-deep shrink-0 mt-0.5" />{f}</li>)}
                                    </ul>
                                </button>
                            );
                        })}
                    </div>
                    <label className="flex items-center gap-3 border border-[#DDD6FE] rounded-sm p-4 bg-[#FCFAFF] cursor-pointer max-w-md" data-testid="senior-discount-label">
                        <input type="checkbox" checked={senior} onChange={(e) => setSenior(e.target.checked)} data-testid="senior-discount-toggle" />
                        <span className="text-sm text-zinc-700">I'm <span className="font-medium">62 or older</span> — apply the senior discount ($14.99/mo on Flex 1GB or Pay-As-You-Go). You may be asked to verify age at activation.</span>
                    </label>
                </div>
            )}

            {/* Step 1: Device */}
            {step === 1 && (
                <div className="max-w-lg border border-[#DDD6FE] rounded-sm bg-paper p-6 space-y-4" data-testid="mobile-device">
                    <div className="flex items-center gap-2"><Smartphone className="w-5 h-5 text-[#4C1D95]" /><h3 className="font-display text-xl">Is your phone eSIM-ready?</h3></div>
                    <p className="text-sm text-zinc-600">Dial <code className="font-mono bg-cream px-1 rounded">*#06#</code> to see your IMEI &amp; EID, or check Settings → About.</p>
                    <Fld label="Device model" value={device.device_model} onChange={(v) => setDevice((d) => ({ ...d, device_model: v }))} placeholder="iPhone 15 / Galaxy S24" testid="device-model" />
                    <Fld label="EID (32 digits)" value={device.eid} onChange={(v) => setDevice((d) => ({ ...d, eid: v }))} placeholder="8904…" testid="device-eid" />
                    <Fld label="IMEI (15 digits, optional)" value={device.imei} onChange={(v) => setDevice((d) => ({ ...d, imei: v }))} placeholder="3569…" testid="device-imei" />
                    <Button onClick={checkDevice} data-testid="device-check-btn" className="bg-[#4C1D95] text-white rounded-sm h-10">Check compatibility</Button>
                    {deviceResult && (
                        <div className={`rounded-sm p-3 text-sm ${deviceResult.esim_supported ? "bg-emerald-deep/10 text-emerald-deep" : "bg-amber-50 text-amber-700"}`} data-testid="device-result">
                            {deviceResult.esim_supported ? <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> eSIM supported — you're good to go.</span> : "We couldn't confirm eSIM support."}
                            {deviceResult.notes?.map((n, i) => <p key={i} className="text-xs mt-1">{n}</p>)}
                        </div>
                    )}
                </div>
            )}

            {/* Step 2: Number */}
            {step === 2 && (
                <div className="space-y-5" data-testid="mobile-number">
                    <div className="flex gap-3">
                        <button onClick={() => setProvisionType("new")} data-testid="choose-new-number"
                            className={`flex-1 border rounded-sm p-4 text-left ${provisionType === "new" ? "border-[#4C1D95] bg-[#F3E8FF]" : "border-[#DDD6FE] bg-paper hover:bg-cream"}`}>
                            <Search className="w-5 h-5 text-[#4C1D95]" /><p className="font-medium mt-2">Get a new number</p><p className="text-xs text-zinc-500">Pick a fresh number</p>
                        </button>
                        <button onClick={() => setProvisionType("port")} data-testid="choose-port-number"
                            className={`flex-1 border rounded-sm p-4 text-left ${provisionType === "port" ? "border-[#4C1D95] bg-[#F3E8FF]" : "border-[#DDD6FE] bg-paper hover:bg-cream"}`}>
                            <ArrowRightLeft className="w-5 h-5 text-[#4C1D95]" /><p className="font-medium mt-2">Transfer my number</p><p className="text-xs text-zinc-500">Keep your existing number</p>
                        </button>
                    </div>

                    {provisionType === "new" && (
                        <div className="border border-[#DDD6FE] rounded-sm bg-paper p-5">
                            <div className="flex gap-2 items-end">
                                <div className="w-40"><Label className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">Area code</Label>
                                    <Input value={areaCode} onChange={(e) => setAreaCode(e.target.value)} placeholder="901" data-testid="area-code-input" className="rounded-sm border-[#DDD6FE] h-10 mt-1.5" /></div>
                                <Button onClick={searchNumbers} disabled={searching} data-testid="search-numbers-btn" className="bg-[#4C1D95] text-white rounded-sm h-10">{searching ? "Searching…" : "Search"}</Button>
                            </div>
                            {numbers.length > 0 && (
                                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-4" data-testid="numbers-grid">
                                    {numbers.map((n) => (
                                        <button key={n.phone_number} onClick={() => setChosenNumber(n.phone_number)} data-testid={`number-${n.phone_number}`}
                                            className={`border rounded-sm p-3 font-mono text-sm ${chosenNumber === n.phone_number ? "border-[#4C1D95] bg-[#F3E8FF]" : "border-[#DDD6FE] hover:bg-cream"}`}>
                                            {n.phone_number}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {provisionType === "port" && (
                        <div className="border border-[#DDD6FE] rounded-sm bg-paper p-5 space-y-4 max-w-lg" data-testid="port-form">
                            <p className="text-sm text-zinc-600">Get a transfer PIN from your current carrier's app, then enter your details.</p>
                            <div className="border border-[#DDD6FE] rounded-sm bg-[#FCFAFF] p-3 text-xs" data-testid="carrier-login-help">
                                <p className="font-medium text-zinc-700 mb-1.5">Need your account # &amp; transfer PIN? Log in to your carrier:</p>
                                <div className="flex flex-wrap gap-x-3 gap-y-1">
                                    {CARRIER_LINKS.map((c) => (
                                        <a key={c.name} href={c.url} target="_blank" rel="noopener noreferrer" className="text-emerald-deep underline" data-testid={`carrier-link-${c.name.toLowerCase().replace(/[^a-z]/g,'')}`}>{c.name} ↗</a>
                                    ))}
                                </div>
                            </div>
                            <Fld label="Phone number to transfer" value={port.phone_number} onChange={(v) => setPort((p) => ({ ...p, phone_number: v }))} placeholder="+19015551234" testid="port-number" />
                            <div className="grid grid-cols-2 gap-3">
                                <Fld label="Current carrier" value={port.port_carrier} onChange={(v) => setPort((p) => ({ ...p, port_carrier: v }))} placeholder="AT&T" testid="port-carrier" />
                                <Fld label="Account number" value={port.port_account_number} onChange={(v) => setPort((p) => ({ ...p, port_account_number: v }))} placeholder="123456" testid="port-account" />
                                <Fld label="Transfer / port PIN" value={port.port_pin} onChange={(v) => setPort((p) => ({ ...p, port_pin: v }))} placeholder="1234" testid="port-pin" />
                                <Fld label="Billing ZIP" value={port.port_zip} onChange={(v) => setPort((p) => ({ ...p, port_zip: v }))} placeholder="38103" testid="port-zip" />
                            </div>
                            <Fld label="Account holder name" value={port.port_name} onChange={(v) => setPort((p) => ({ ...p, port_name: v }))} placeholder="Full name on account" testid="port-name" />
                            <Fld label="Service address (street) on your current account" value={port.port_street} onChange={(v) => setPort((p) => ({ ...p, port_street: v }))} placeholder="123 Main St" testid="port-street" />
                            <div className="grid grid-cols-2 gap-3">
                                <Fld label="City" value={port.port_city} onChange={(v) => setPort((p) => ({ ...p, port_city: v }))} placeholder="Memphis" testid="port-city" />
                                <Fld label="State" value={port.port_state} onChange={(v) => setPort((p) => ({ ...p, port_state: v.toUpperCase().slice(0, 2) }))} placeholder="TN" testid="port-state" />
                            </div>
                            <div className="text-xs" data-testid="port-bill-upload">
                                <p className="font-medium text-zinc-700 mb-1">Copy of your latest phone bill <span className="text-zinc-500 font-normal">(required — within the last 90 days; carriers need it to release your number)</span></p>
                                <input type="file" accept=".pdf,.jpg,.jpeg,.png" data-testid="port-bill-input" disabled={billUp.busy}
                                    onChange={async (e) => {
                                        const f = e.target.files?.[0]; if (!f) return;
                                        setBillUp((b) => ({ ...b, busy: true }));
                                        try {
                                            const fd = new FormData(); fd.append("file", f);
                                            const { data } = await api.post("/mobile/port-bill", fd);
                                            setBillUp({ path: data.path, name: f.name, busy: false }); toast.success("Bill attached");
                                        } catch (err) { setBillUp((b) => ({ ...b, busy: false })); toast.error(err?.response?.data?.detail || "Upload failed"); }
                                    }} />
                                {billUp.busy && <span className="ml-2 text-zinc-500">Uploading…</span>}
                                {billUp.path && <span className="ml-2 text-emerald-deep" data-testid="port-bill-attached">Attached: {billUp.name}</span>}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Step 3: Review */}
            {step === 3 && (
                <div className="max-w-lg border border-[#DDD6FE] rounded-sm bg-paper p-6 space-y-3" data-testid="mobile-review">
                    <h3 className="font-display text-xl">Review &amp; activate</h3>
                    {(() => { const p = plans.find((x) => x.id === planId) || {}; const eff = (senior && p.senior_price) ? p.senior_price : p.price; return (
                        <>
                            <RevRow k="Plan" v={`${p.name} — $${eff}/mo${(senior && p.senior_price) ? " (Senior 62+)" : ""}`} />
                            <RevRow k="Data" v={`${p.data} · ${p.speed}`} />
                            <RevRow k="Number" v={provisionType === "new" ? chosenNumber : `${port.phone_number} (transfer)`} />
                            <RevRow k="Device" v={device.device_model || "eSIM device"} />
                            <RevRow k="Regulatory, 911 & admin fee" v={`$${fees.regulatory_monthly}/mo`} />
                            <RevRow k="One-time activation fee" v={`$${fees.activation_one_time}`} />
                            {p.payg && <RevRow k="Data pricing" v={`$${fees.data_per_gb}/GB used, added to your next bill (up to 5 GB/mo) — or buy prepaid 1 GB packs`} />}
                            <p className="text-sm font-medium pt-2" data-testid="mobile-due-today">Due today: ${(Number(eff) + fees.regulatory_monthly + fees.activation_one_time).toFixed(2)} · then ${(Number(eff) + fees.regulatory_monthly).toFixed(2)}/mo + data used</p>
                            <p className="text-xs text-zinc-500">Cancel anytime. Your eSIM is issued right after payment.</p>
                            <div className="pt-2" data-testid="mobile-promo-row">
                                <label className="text-xs font-medium text-zinc-600">Promo code</label>
                                <div className="flex gap-2 mt-1">
                                    <input
                                        value={promo}
                                        onChange={(e) => setPromo(e.target.value.toUpperCase())}
                                        placeholder="e.g. SAVE10"
                                        data-testid="mobile-promo-input"
                                        className="flex-1 border border-[#DDD6FE] rounded-sm px-3 h-10 text-sm bg-white outline-none focus:border-emerald-deep"
                                    />
                                    <Button type="button" variant="outline" onClick={() => applyPromo()} data-testid="mobile-promo-apply" className="rounded-sm border-[#DDD6FE] h-10">Apply</Button>
                                </div>
                                {promoInfo?.valid && <p className="text-xs text-emerald-deep mt-1.5" data-testid="mobile-promo-applied">✓ {promoInfo.label} applied to your bill.</p>}
                            </div>
                            {provisionType === "port" && <LoaSign value={loaSig} onChange={setLoaSig} />}
                            <Button onClick={checkout} disabled={checkingOut || (provisionType === "port" && !(loaSig.agreed && loaSig.name.trim().length >= 3))} data-testid="mobile-checkout-btn" className="w-full bg-emerald-deep text-white rounded-sm h-11 mt-2">
                                {checkingOut ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Pay ${(() => { const p = plans.find((x) => x.id === planId) || {}; const e = (senior && p.senior_price) ? p.senior_price : p.price; return (Number(e || 0) + (p.legacy ? 0 : fees.regulatory_monthly + fees.activation_one_time)).toFixed(2); })()} &amp; activate <ArrowRight className="w-4 h-4 ml-1" /></>}
                            </Button>
                        </>
                    ); })()}
                </div>
            )}

            {/* Nav buttons */}
            <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
                    data-testid="mobile-back-btn" className="rounded-sm border-[#DDD6FE]"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
                {step < 3 && (
                    <Button onClick={() => setStep((s) => s + 1)} disabled={!canNext} data-testid="mobile-next-btn"
                        className="bg-[#4C1D95] text-white rounded-sm">Continue <ArrowRight className="w-4 h-4 ml-1" /></Button>
                )}
            </div>
        </div>
    );
}

function LinesView({ subs, plans, selectedId, onSelect, onAdd }) {
    const sub = subs.find((s) => s.mobile_sub_id === selectedId) || subs[0];
    return (
        <div className="space-y-6" data-testid="mobile-lines-view">
            <div className="flex flex-wrap items-center gap-2" data-testid="mobile-line-switcher">
                {subs.map((s, i) => {
                    const on = s.mobile_sub_id === sub.mobile_sub_id;
                    return (
                        <button key={s.mobile_sub_id} onClick={() => onSelect(s.mobile_sub_id)} data-testid={`mobile-line-tab-${s.mobile_sub_id}`}
                            className={`inline-flex items-center gap-2 rounded-sm border h-10 px-4 text-sm transition-colors ${on ? "bg-[#4C1D95] text-white border-[#4C1D95]" : "bg-paper border-[#DDD6FE] hover:border-[#4C1D95]"}`}>
                            <Smartphone className="w-4 h-4" /> Line {i + 1}
                            <span className={`font-mono text-xs ${on ? "text-white/80" : "text-zinc-500"}`}>{(s.native_linked ? s.phone_number : s.esim?.native_number || s.phone_number) || "pending"}</span>
                        </button>
                    );
                })}
                <Button onClick={onAdd} data-testid="mobile-add-line-btn" className="bg-brand-green text-white rounded-sm h-10 px-4">
                    <Plus className="w-4 h-4 mr-1" /> Add a line
                </Button>
            </div>
            <MobileDashboard key={sub.mobile_sub_id} sub={sub} plans={plans} />
        </div>
    );
}

function PayToRestore({ sub }) {
    const [busy, setBusy] = useState(false);
    const pay = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/mobile/subscription/${sub.mobile_sub_id}/pay`);
            if (data.url) { window.location.href = data.url; return; }
            toast.success("You're paid up — service is restoring now");
            setTimeout(() => window.location.reload(), 1500);
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not start payment"); }
        setBusy(false);
    };
    return (
        <div className="rounded-sm bg-red-50 border border-[#D97706]/30 px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="mobile-pay-to-restore">
            <div>
                <p className="font-medium text-[#D97706]">Service paused — payment required</p>
                <p className="text-sm text-zinc-600">{sub.unpaid_hold ? "There's no payment on file for this line." : "Your last payment didn't go through."} Pay now and your service turns back on automatically.</p>
            </div>
            <Button onClick={pay} disabled={busy} data-testid="mobile-pay-now-btn" className="bg-[#D97706] text-white rounded-sm h-10 px-5 shrink-0">{busy ? "Opening…" : "Pay now"}</Button>
        </div>
    );
}

function MobileDashboard({ sub, plans }) {
    const plan = plans.find((p) => p.id === sub.plan_id) || {};
    const esim = sub.esim || {};
    const [usage, setUsage] = useState(sub.usage || null);
    const [esimData, setEsimData] = useState(esim);
    const [portStatus, setPortStatus] = useState(sub.port?.status);
    const [focDate, setFocDate] = useState(sub.port?.foc_date || null);
    const [refreshing, setRefreshing] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/mobile/subscription/${sub.mobile_sub_id}/usage`);
                setUsage(data.usage);
            } catch { /* ignore */ }
        })();
        if (sub.provision_type === "port") {
            (async () => {
                try {
                    const { data } = await api.get(`/mobile/subscription/${sub.mobile_sub_id}/port`);
                    if (data.status) setPortStatus(data.status);
                    if (data.foc_date) setFocDate(data.foc_date);
                } catch { /* ignore */ }
            })();
        }
    }, [sub.mobile_sub_id]);

    const refreshEsim = async () => {
        setRefreshing(true);
        try {
            const { data } = await api.post(`/mobile/subscription/${sub.mobile_sub_id}/esim/refresh`);
            setEsimData(data.esim || {});
            toast.success("eSIM status refreshed");
        } catch { toast.error("Could not refresh eSIM"); }
        setRefreshing(false);
    };

    const refreshPort = async () => {
        try {
            const { data } = await api.get(`/mobile/subscription/${sub.mobile_sub_id}/port`);
            setPortStatus(data.status);
            if (data.foc_date) setFocDate(data.foc_date);
        } catch { /* ignore */ }
    };

    const pct = usage?.percent;
    return (
        <div className="space-y-6" data-testid="mobile-dashboard">
            <EsimOnboarding subId={sub.mobile_sub_id} qrLpa={esimData.qr_lpa} />
            <div>
                <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-deep mb-2 flex items-center gap-1.5"><Signal className="w-3.5 h-3.5" /> Flex Line Mobile</p>
                <h1 className="font-display text-4xl font-light tracking-tight">Your line is <span className="font-serif-italic">{sub.status === "past_due" ? "paused." : "active."}</span></h1>
            </div>
            {sub.status === "past_due" && <PayToRestore sub={sub} />}
            {sub.provision_type === "port" && !sub.activation?.service_active && focDate && (() => {
                const days = Math.ceil((new Date(focDate) - new Date()) / 86400000);
                const label = days > 1 ? `Your number transfers in ${days} days`
                    : days === 1 ? "Your number transfers tomorrow"
                    : days === 0 ? "Your number transfers today"
                    : "Your number transfer is completing now";
                const dateStr = new Date(focDate).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
                return (
                    <div className="rounded-sm bg-[#F5F0FF] border border-[#4C1D95]/20 px-5 py-4 flex items-center gap-4" data-testid="port-countdown-badge">
                        <div className="w-12 h-12 rounded-sm bg-[#4C1D95] text-white grid place-items-center shrink-0">
                            <span className="font-display text-xl leading-none" data-testid="port-countdown-days">{days > 0 ? days : "!"}</span>
                        </div>
                        <div>
                            <p className="font-medium text-[#4C1D95]">{label}</p>
                            <p className="text-sm text-zinc-600">Scheduled completion (FOC): {dateStr}. Scan your eSIM QR now so you're ready.</p>
                        </div>
                    </div>
                );
            })()}
            {sub.provisioning_mode === "test" && (
                <div className="rounded-sm bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-2" data-testid="mobile-test-banner">
                    Test mode — this eSIM &amp; data usage are simulated. Enable live provisioning (and top up Telnyx) to issue a real eSIM.
                </div>
            )}
            <div className="grid md:grid-cols-3 gap-4">
                <Stat label="Phone number" value={(sub.native_linked ? sub.phone_number : esimData.native_number || sub.phone_number) || "—"} icon={Smartphone} testid="mobile-line-number" />
                <Stat label="Plan" value={`${plan.name || sub.plan_name} · $${plan.price || sub.plan_price}/mo`} icon={Zap} />
                <Stat label="Data" value={plan.data || sub.data || "—"} icon={Wifi} />
            </div>

            {sub.native_linked && (
                <p className="text-xs text-emerald-deep -mt-3" data-testid="mobile-native-linked">Your number rings this phone directly — calls from your dial pad show {sub.phone_number}.</p>
            )}
            {!sub.native_linked && esimData.native_number && sub.phone_number && esimData.native_number !== sub.phone_number && (
                <p className="text-xs text-zinc-500 -mt-3" data-testid="mobile-backup-number">Backup calling (in-app) number: <span className="font-mono">{sub.phone_number}</span></p>
            )}
            {esimData.delivered_at && (
                <p className="text-xs text-emerald-deep -mt-3" data-testid="mobile-esim-emailed">Install link emailed to you on {new Date(esimData.delivered_at).toLocaleDateString()}.</p>
            )}
            {["needs_attention", "exception", "error"].includes(sub.port?.status) && <PortFix sub={sub} />}
            <ActivationTimeline activation={sub.activation} />

            {/* Data usage */}
            {!sub.payg && <div className="border border-[#DDD6FE] rounded-sm bg-paper p-6" data-testid="mobile-usage-card">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-display text-xl">Data usage</h3>
                    <span className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">This billing period</span>
                </div>
                {usage ? (
                    <div>
                        <div className="flex items-baseline justify-between mb-2">
                            <span className="font-display text-2xl font-light" data-testid="usage-consumed">{usage.consumed_gb} GB</span>
                            <span className="text-sm text-zinc-500">{usage.unlimited ? "of Unlimited" : `of ${usage.limit_gb} GB`}</span>
                        </div>
                        {!usage.unlimited && (
                            <div className="h-2.5 rounded-full bg-[#F3E8FF] overflow-hidden">
                                <div className={`h-full rounded-full transition-all ${pct >= 90 ? "bg-[#D97706]" : "bg-emerald-deep"}`} style={{ width: `${pct || 0}%` }} data-testid="usage-bar" />
                            </div>
                        )}
                        {usage.unlimited && <p className="text-sm text-emerald-deep">Unlimited high-speed data on this plan.</p>}
                        {!usage.unlimited && pct >= 90 && <p className="text-xs text-[#D97706] mt-2">You're close to your data limit — consider upgrading your plan.</p>}
                    </div>
                ) : <p className="text-sm text-zinc-500">Loading usage…</p>}
            </div>}

            {sub.payg && <PaygCard sub={sub} />}
            {sub.legacy && <SwitchToPayg sub={sub} />}
            {sub.provision_type === "port" && <PortTracker port={sub.port || {}} />}
            <div className="border border-[#DDD6FE] rounded-sm bg-paper p-6" data-testid="mobile-esim-card">
                <div className="flex items-center justify-between mb-1">
                    <h3 className="font-display text-xl">Install your eSIM</h3>
                    <Button variant="outline" onClick={refreshEsim} disabled={refreshing} data-testid="esim-refresh-btn" className="rounded-sm border-[#DDD6FE] h-8 text-xs">
                        {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Refresh"}
                    </Button>
                </div>
                <p className="text-sm text-zinc-600 mb-4">One-tap install: on iPhone (iOS 17.4+) or Android (10+), tap your device's button below and confirm — no QR scan needed. Otherwise, scan the QR in Settings → Mobile/Cellular → Add eSIM, or enter the code manually.</p>
                {esimData.qr_lpa && (
                    <div className="flex flex-wrap gap-2 mb-4">
                        <a
                            href={`https://esimsetup.apple.com/esim_qrcode_provisioning?carddata=${encodeURIComponent(esimData.qr_lpa)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="esim-one-tap-install"
                            className="inline-flex items-center gap-2 bg-[#4C1D95] text-white rounded-sm px-5 h-11 text-sm font-medium hover:bg-[#4C1D95]/90 transition-colors"
                        >
                            <Smartphone className="w-4 h-4" /> Install on iPhone
                        </a>
                        <a
                            href={`https://esimsetup.android.com/esim_qrcode_provisioning?carddata=${encodeURIComponent(esimData.qr_lpa)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="esim-one-tap-install-android"
                            className="inline-flex items-center gap-2 bg-brand-green text-white rounded-sm px-5 h-11 text-sm font-medium hover:opacity-90 transition-opacity"
                        >
                            <Smartphone className="w-4 h-4" /> Install on Android
                        </a>
                    </div>
                )}
                <div className="flex flex-col sm:flex-row gap-6 items-start">
                    <div className="p-3 bg-white border border-[#DDD6FE] rounded-sm">
                        {esimData.qr_lpa ? <QRCodeSVG value={esimData.qr_lpa} size={168} fgColor="#4C1D95" /> : <div className="w-[168px] h-[168px] flex items-center justify-center text-zinc-400 text-xs text-center px-4">eSIM provisioning — tap Refresh in a moment</div>}
                    </div>
                    <div className="flex-1 space-y-2 text-sm">
                        <div><span className="font-mono text-[11px] uppercase text-zinc-500">eSIM status</span><p className="font-medium capitalize" data-testid="esim-status">{esimData.status || "pending"}</p></div>
                        {esimData.activation_code && <div><span className="font-mono text-[11px] uppercase text-zinc-500">Activation code</span><p className="font-mono text-xs break-all bg-cream rounded px-2 py-1 mt-1" data-testid="esim-activation-code">{esimData.activation_code}</p></div>}
                        {sub.provision_type === "port" && (
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="font-mono text-[11px] uppercase text-zinc-500">Transfer status</span>
                                    <button onClick={refreshPort} data-testid="port-refresh-btn" className="text-[11px] text-[#4C1D95] underline">refresh</button>
                                </div>
                                <p className="font-medium capitalize" data-testid="port-status">{({ pending_payment: "Waiting for payment", pending_carrier: "Preparing transfer", submitted_to_carrier: "Sent to your carrier", "in-process": "Carrier reviewing", submitted: "Carrier reviewing", needs_attention: "Needs your attention", exception: "Needs your attention", ported: "Transferred", completed: "Transferred", error: "Needs your attention" })[portStatus] || (portStatus || "processing").replace(/_/g, " ")}</p>
                                <div className="mt-2">
                                    <span className="font-mono text-[11px] uppercase text-zinc-500">Est. completion date (FOC)</span>
                                    <p className="font-medium" data-testid="port-foc-date">
                                        {focDate
                                            ? new Date(focDate).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })
                                            : "Scheduling — usually within 24 hours"}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                {/* Guided install steps */}
                <div className="mt-5 border-t border-[#DDD6FE]/60 pt-4" data-testid="esim-install-guide">
                    <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-3">Guided install</p>
                    <ol className="grid sm:grid-cols-3 gap-3">
                        {[
                            { n: 1, t: "Scan QR / enter code", d: "Settings → Cellular → Add eSIM." },
                            { n: 2, t: "Installing…", d: "Your phone downloads the eSIM profile." },
                            { n: 3, t: "Activated — restart", d: "Restart your device to connect." },
                        ].map((s) => (
                            <li key={s.n} className="flex gap-3 items-start border border-[#DDD6FE] rounded-sm p-3">
                                <span className="w-6 h-6 rounded-full bg-[#4C1D95] text-white grid place-items-center text-xs font-mono shrink-0">{s.n}</span>
                                <div><p className="font-medium text-sm leading-tight">{s.t}</p><p className="text-xs text-zinc-500 mt-0.5">{s.d}</p></div>
                            </li>
                        ))}
                    </ol>
                </div>
            </div>

            {/* Physical SIM activation (Phase B) */}
            <PhysicalSimCard sub={sub} />
        </div>
    );
}

function PhysicalSimCard({ sub }) {
    const [open, setOpen] = useState(!!(sub.physical_sim && sub.physical_sim.status === "active"));
    const [iccid, setIccid] = useState(sub.physical_sim?.iccid || "");
    const [imei, setImei] = useState(sub.physical_sim?.imei || "");
    const [status, setStatus] = useState(sub.physical_sim?.status || null);
    const [busy, setBusy] = useState(false);

    const activate = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/mobile/subscription/${sub.mobile_sub_id}/activate-sim`, { iccid, imei });
            setStatus(data.sim.status);
            toast.success("SIM activated");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not activate SIM");
        }
        setBusy(false);
    };

    return (
        <div className="border border-[#DDD6FE] rounded-sm bg-paper p-6" data-testid="physical-sim-card">
            <button onClick={() => setOpen((o) => !o)} className="flex items-center justify-between w-full text-left" data-testid="physical-sim-toggle">
                <div>
                    <h3 className="font-display text-xl">Using a physical SIM instead?</h3>
                    <p className="text-sm text-zinc-600 mt-0.5">Activate a physical SIM with its ICCID and your phone's IMEI.</p>
                </div>
                <span className="text-zinc-400 text-sm">{open ? "−" : "+"}</span>
            </button>
            {open && (
                <div className="mt-4 grid sm:grid-cols-2 gap-3 max-w-xl">
                    <div>
                        <label className="font-mono text-[11px] uppercase text-zinc-500">ICCID (on the SIM)</label>
                        <input value={iccid} onChange={(e) => setIccid(e.target.value)} data-testid="sim-iccid-input"
                            placeholder="8901…" className="w-full mt-1 border border-[#DDD6FE] rounded-sm h-10 px-3 text-sm" />
                    </div>
                    <div>
                        <label className="font-mono text-[11px] uppercase text-zinc-500">IMEI (dial *#06#)</label>
                        <input value={imei} onChange={(e) => setImei(e.target.value)} data-testid="sim-imei-input"
                            placeholder="15 digits" className="w-full mt-1 border border-[#DDD6FE] rounded-sm h-10 px-3 text-sm" />
                    </div>
                    <div className="sm:col-span-2 flex items-center gap-3">
                        <Button onClick={activate} disabled={busy || !iccid || !imei} data-testid="sim-activate-btn"
                            className="bg-emerald-deep text-white rounded-sm h-10 px-6">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Activate SIM"}
                        </Button>
                        {status && <span className="text-sm capitalize" data-testid="sim-status">Status: <span className="font-medium">{status}</span></span>}
                    </div>
                </div>
            )}
        </div>
    );
}

function PaygCard({ sub }) {
    const m = sub.meter || {};
    const [qty, setQty] = useState(1);
    const [busy, setBusy] = useState(false);
    const buy = async () => {
        setBusy(true);
        try { const { data } = await api.post(`/mobile/subscription/${sub.mobile_sub_id}/data-pack`, { quantity: qty }); window.location.href = data.url; }
        catch (e) { toast.error(e?.response?.data?.detail || "Couldn't start checkout"); setBusy(false); }
    };
    const gb = (mb) => (Number(mb || 0) / 1024).toFixed(2);
    const [auto, setAuto] = useState(!!sub.auto_topup);
    const toggleAuto = async (v) => {
        try { await api.post(`/mobile/subscription/${sub.mobile_sub_id}/auto-topup`, { enabled: v }); setAuto(v); toast.success(v ? "Auto top-up on" : "Auto top-up off"); }
        catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update auto top-up"); }
    };
    return (
        <div className="border border-[#DDD6FE] rounded-sm bg-paper p-6 space-y-3" data-testid="payg-card">
            <h3 className="font-display text-xl">Data this month</h3>
            <div className="grid grid-cols-3 gap-3 text-sm">
                <div><p className="text-zinc-500 text-xs">Used</p><p className="font-mono" data-testid="payg-used">{gb(m.used_mb)} GB</p></div>
                <div><p className="text-zinc-500 text-xs">Prepaid left</p><p className="font-mono" data-testid="payg-prepaid">{gb(m.prepaid_mb)} GB</p></div>
                <div><p className="text-zinc-500 text-xs">Added to next bill</p><p className="font-mono" data-testid="payg-billed">${Number(m.billed_usd || 0).toFixed(2)}</p></div>
            </div>
            <p className="text-xs text-zinc-500">Data is ${sub.data_price_per_gb}/GB — prepaid packs are used first, the rest is added to your next bill (up to 5 GB/mo).</p>
            <div className="flex items-center gap-2">
                <select value={qty} onChange={(e) => setQty(Number(e.target.value))} data-testid="payg-pack-qty" className="border border-[#DDD6FE] rounded-sm h-10 px-2 text-sm">
                    {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n} GB — ${(n * sub.data_price_per_gb).toFixed(2)}</option>)}
                </select>
                <Button onClick={buy} disabled={busy} data-testid="payg-buy-pack" className="bg-[#4C1D95] text-white rounded-sm h-10">{busy ? "Opening…" : "Buy prepaid data"}</Button>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={auto} onChange={(e) => toggleAuto(e.target.checked)} data-testid="payg-auto-topup" />
                Auto top-up: buy 1 GB (${sub.data_price_per_gb}) automatically when my prepaid data runs out
            </label>
            <p className="text-xs text-zinc-500">We'll text and email you at 1, 3 and 5 GB used.</p>
        </div>
    );
}

function SwitchToPayg({ sub }) {
    const [busy, setBusy] = useState(false);
    const go = async () => {
        if (!window.confirm("Switch to Flex Pay-As-You-Go: $19.99/mo + $3.99 regulatory fee, data $15/GB used (prepaid packs available). Your bill is prorated. Continue?")) return;
        setBusy(true);
        try { await api.post(`/mobile/subscription/${sub.mobile_sub_id}/change-plan`, { plan_id: "flex_payg" }); toast.success("You're now on pay-as-you-go"); setTimeout(() => window.location.reload(), 1000); }
        catch (e) { toast.error(e?.response?.data?.detail || "Couldn't switch plans"); setBusy(false); }
    };
    return (
        <div className="border border-[#DDD6FE] rounded-sm bg-[#FCFAFF] p-5 flex items-center justify-between gap-4" data-testid="switch-payg">
            <div>
                <p className="font-medium">Only pay for the data you use</p>
                <p className="text-sm text-zinc-600">Switch to Flex Pay-As-You-Go — $19.99/mo + data at $15/GB, with prepaid packs and usage alerts.</p>
            </div>
            <Button onClick={go} disabled={busy} data-testid="switch-payg-btn" variant="outline" className="rounded-sm h-10 border-[#4C1D95] text-[#4C1D95] shrink-0">{busy ? "Switching…" : "Switch plan"}</Button>
        </div>
    );
}

const PORT_STEPS = [
    ["signed", "Authorization signed"], ["submitted", "Sent to your carrier"], ["review", "Carrier reviewing"],
    ["scheduled", "Transfer date set"], ["done", "Number transferred"],
];

function PortTracker({ port }) {
    const s = port.status || "";
    const idx = ["ported", "completed"].includes(s) ? 4 : port.foc_date ? 3 : ["in-process", "submitted"].includes(s) ? 2
        : s === "submitted_to_carrier" ? 1 : 0;
    const bad = ["needs_attention", "exception", "error", "cancelled", "failed"].includes(s);
    return (
        <div className="border border-[#DDD6FE] rounded-sm bg-paper p-6" data-testid="port-tracker">
            <h3 className="font-display text-xl mb-3">Number transfer</h3>
            <ol className="space-y-2">
                {PORT_STEPS.map(([k, label], i) => (
                    <li key={k} className="flex items-center gap-2 text-sm" data-testid={`port-step-${k}`}>
                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${i <= idx ? (bad && i === idx ? "bg-[#D97706] text-white" : "bg-emerald-deep text-white") : "bg-[#F3E8FF] text-zinc-500"}`}>{i < idx || (i === idx && !bad && idx > 0) ? "✓" : i + 1}</span>
                        <span className={i <= idx ? "text-zinc-900" : "text-zinc-500"}>{label}{k === "scheduled" && port.foc_date ? ` — ${new Date(port.foc_date).toLocaleDateString()}` : ""}</span>
                    </li>
                ))}
            </ol>
            {bad && <p className="text-xs text-[#D97706] mt-3" data-testid="port-tracker-problem">Needs your attention — see the details above.</p>}
        </div>
    );
}

function LoaSign({ value, onChange }) {
    const [loa, setLoa] = useState(null);
    useEffect(() => { api.get("/mobile/loa").then(({ data }) => setLoa(data)).catch(() => {}); }, []);
    return (
        <div className="border border-[#DDD6FE] rounded-sm bg-[#FCFAFF] p-4 space-y-3" data-testid="loa-sign">
            <p className="font-medium text-sm">Letter of Agency — authorize your number transfer</p>
            <p className="text-xs text-zinc-600 leading-relaxed max-h-36 overflow-y-auto" data-testid="loa-text">{loa?.text || "Loading…"}</p>
            <Fld label="Type your full legal name to e-sign" value={value.name} onChange={(v) => onChange({ ...value, name: v })} placeholder="Jane Q. Customer" testid="loa-signature" />
            <label className="flex items-start gap-2 text-xs text-zinc-700 cursor-pointer">
                <input type="checkbox" checked={value.agreed} onChange={(e) => onChange({ ...value, agreed: e.target.checked })} data-testid="loa-agree" className="mt-0.5" />
                I am the account holder (or authorized), I have read the Letter of Agency, and I agree my typed name is my electronic signature.
            </label>
            {value.name && <p className="font-serif-italic text-xl text-[#4C1D95]" data-testid="loa-signature-preview">{value.name}</p>}
        </div>
    );
}

function PortFix({ sub }) {
    const p = sub.port || {};
    const [f, setF] = useState({ port_name: p.name || "", port_street: p.street || "", port_city: p.city || "", port_state: p.state || "", port_zip: p.zip || "", port_account_number: "", port_pin: "" });
    const [sig, setSig] = useState({ name: "", agreed: false });
    const [bill, setBill] = useState("");
    const [busy, setBusy] = useState(false);
    const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
    const ok = Object.values(f).every((v) => v.trim()) && sig.agreed && sig.name.trim().length >= 3;
    const submit = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/mobile/${sub.mobile_sub_id}/port/resubmit`, { ...f, loa_signature: sig.name, loa_agreed: sig.agreed, port_bill_path: bill });
            if (data.ok || data.status === "pending_carrier") { toast.success("Transfer resubmitted to your carrier"); setTimeout(() => window.location.reload(), 1200); }
            else toast.error(data.error || "The carrier still couldn't accept these details");
        } catch (e) { toast.error(e?.response?.data?.detail || "Resubmit failed"); }
        setBusy(false);
    };
    return (
        <div className="border border-[#D97706] bg-[#FEF3C7] rounded-sm p-5 space-y-3" data-testid="port-fix">
            <p className="font-medium">Your number transfer needs a fix</p>
            <p className="text-sm text-zinc-700" data-testid="port-fix-reason">{(p.error || "Your current carrier couldn't match the details").replace(/[.\s]+$/, "")}. Enter them exactly as your current carrier has them.</p>
            <div className="grid grid-cols-2 gap-3">
                <Fld label="Account holder name" value={f.port_name} onChange={set("port_name")} testid="fix-name" />
                <Fld label="Account number" value={f.port_account_number} onChange={set("port_account_number")} testid="fix-account" />
                <Fld label="Transfer PIN" value={f.port_pin} onChange={set("port_pin")} testid="fix-pin" />
                <Fld label="Street" value={f.port_street} onChange={set("port_street")} testid="fix-street" />
                <Fld label="City" value={f.port_city} onChange={set("port_city")} testid="fix-city" />
                <Fld label="State" value={f.port_state} onChange={(v) => set("port_state")(v.toUpperCase().slice(0, 2))} testid="fix-state" />
                <Fld label="ZIP" value={f.port_zip} onChange={set("port_zip")} testid="fix-zip" />
            </div>
            <div className="text-xs">
                <p className="font-medium text-zinc-700 mb-1">{p.bill_attached ? "Replace your phone bill (optional)" : "Upload your latest phone bill (within 90 days)"}</p>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" data-testid="fix-bill-input" onChange={async (e) => {
                    const file = e.target.files?.[0]; if (!file) return;
                    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/mobile/port-bill", fd); setBill(data.path); toast.success("Bill attached"); }
                    catch (er) { toast.error(er?.response?.data?.detail || "Upload failed"); }
                }} />
            </div>
            <LoaSign value={sig} onChange={setSig} />
            <Button onClick={submit} disabled={!ok || busy || (!p.bill_attached && !bill)} data-testid="port-fix-submit" className="bg-[#4C1D95] text-white rounded-sm h-10">
                {busy ? "Resubmitting…" : "Resubmit transfer"}
            </Button>
        </div>
    );
}

function Fld({ label, value, onChange, placeholder, testid }) {
    return (
        <div className="space-y-1.5">
            <Label className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">{label}</Label>
            <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={`${testid}-input`} className="rounded-sm border-[#DDD6FE] h-10" />
        </div>
    );
}

function RevRow({ k, v }) {
    return <div className="flex justify-between border-b border-[#EAE4D9] py-2 text-sm"><span className="text-zinc-500">{k}</span><span className="font-medium text-right">{v}</span></div>;
}

function Stat({ label, value, icon: Icon, testid }) {
    return (
        <div className="border border-[#DDD6FE] rounded-sm bg-paper p-4" data-testid={testid}>
            <div className="flex items-center gap-2 text-zinc-500"><Icon className="w-4 h-4" /><span className="font-mono text-[11px] uppercase tracking-wider">{label}</span></div>
            <div className="font-display text-lg mt-1 break-all">{value}</div>
        </div>
    );
}
